import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Leaflet Icon Fix für Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

function AddressValidator({ address, onValidationResult, onAddressSelect }) {
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    // Debounce für Adressvalidierung
    const timeoutId = setTimeout(() => {
      if (address && address.trim().length > 5) {
        validateAddress(address);
      } else {
        setValidationResult(null);
        setSuggestions([]);
        setShowMap(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [address]);

  const validateAddress = async (addr) => {
    setIsValidating(true);
    setValidationResult(null);
    setSuggestions([]);

    try {
      // Nominatim Geocoding API verwenden
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addr)}&limit=5&addressdetails=1&countrycodes=de`,
        {
          headers: {
            'User-Agent': 'CRM-System/1.0'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Fehler bei der Adressvalidierung');
      }

      const data = await response.json();

      if (data && data.length > 0) {
        setSuggestions(data);
        setValidationResult({
          valid: true,
          message: `${data.length} Adresse(n) gefunden`,
          results: data
        });
        onValidationResult({ valid: true, results: data });
      } else {
        setValidationResult({
          valid: false,
          message: 'Keine Adresse gefunden. Bitte überprüfen Sie die Eingabe.'
        });
        onValidationResult({ valid: false, results: [] });
      }
    } catch (error) {
      console.error('Adressvalidierung Fehler:', error);
      setValidationResult({
        valid: false,
        message: 'Fehler bei der Adressvalidierung. Bitte versuchen Sie es erneut.'
      });
      onValidationResult({ valid: false, results: [] });
    } finally {
      setIsValidating(false);
    }
  };

  const handleSelectAddress = (result) => {
    const fullAddress = result.display_name;
    if (onAddressSelect) {
      onAddressSelect(fullAddress);
    }
    // Validierung als erfolgreich markieren
    setValidationResult({
      valid: true,
      message: 'Adresse ausgewählt',
      results: [result]
    });
    onValidationResult({ valid: true, results: [result] });
    showAddressOnMap(result);
  };

  const showAddressOnMap = (result) => {
    if (!mapRef.current) return;

    if (!mapInstanceRef.current) {
      // Karte initialisieren
      mapInstanceRef.current = L.map(mapRef.current).setView(
        [parseFloat(result.lat), parseFloat(result.lon)],
        15
      );

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(mapInstanceRef.current);
    } else {
      mapInstanceRef.current.setView([parseFloat(result.lat), parseFloat(result.lon)], 15);
    }

    // Marker hinzufügen/aktualisieren
    if (markerRef.current) {
      mapInstanceRef.current.removeLayer(markerRef.current);
    }

    markerRef.current = L.marker([parseFloat(result.lat), parseFloat(result.lon)])
      .addTo(mapInstanceRef.current)
      .bindPopup(result.display_name)
      .openPopup();

    setShowMap(true);
    
    // Karte nach kurzer Verzögerung neu zeichnen, um Layout-Probleme zu vermeiden
    setTimeout(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize();
      }
    }, 100);
  };

  useEffect(() => {
    // Cleanup beim Unmount
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  if (!address || address.trim().length <= 5) {
    return null;
  }

  return (
    <div style={{ marginTop: '8px' }}>
      {isValidating && (
        <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '8px' }}>
          Adresse wird überprüft...
        </div>
      )}

      {validationResult && !isValidating && (
        <div style={{ marginBottom: '8px' }}>
          <div
            style={{
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: validationResult.valid ? '#d1fae5' : '#fee2e2',
              color: validationResult.valid ? '#065f46' : '#991b1b',
              fontSize: '12px',
              marginBottom: validationResult.valid && suggestions.length > 0 ? '8px' : '0'
            }}
          >
            {validationResult.message}
          </div>

          {validationResult.valid && suggestions.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>
                Gefundene Adressen:
              </div>
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  onClick={() => handleSelectAddress(suggestion)}
                  style={{
                    padding: '8px',
                    marginBottom: '4px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: '#f8fafc',
                    fontSize: '12px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#e2e8f0'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f8fafc'}
                >
                  {suggestion.display_name}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showMap && mapInstanceRef.current && (
        <div style={{ marginTop: '12px' }}>
          <div
            style={{
              height: '200px',
              width: '100%',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              overflow: 'hidden'
            }}
            ref={mapRef}
          />
        </div>
      )}
    </div>
  );
}

export default AddressValidator;
